<template>
    <div>
        <div class="button-container">
            <button @click="openModal"
                    id="btn-search"
                    class="text-gray-500 p-5 border bg-white rounded-lg mb-4 w-full">
                {{ searchFieldPlaceholder }}
            </button>
        </div>
        <fullscreen-modal :open="isModalOpen" @closed="closeModal">
            <template #modelcontent>
                <div class="input-container">
                    <input
                        type="text"
                        :placeholder="searchFieldPlaceholder"
                        v-model="keyword"
                        class="text-sm appearance-none shadow-sm rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-uknar-primary focus:border-uknar-primary focus:z-10 sm:text-sm">
                </div>
                <div class="mb-3 suggestion-container overflow-y-scroll">
                    <h2 class="mt-5 font-bold">Buildings</h2>
                    <ul>
                        <li :class="[{ 'border-uknar-primary': isBuildingSelected(building) }, 'text-sm border py-2 px-4 mt-2']"
                            @click="handleBuildingSelect(building)"
                            v-for="building of filteredBuildings">
                            {{ building }}

                            <span class="selected-building" v-show="selectedBuildings.indexOf(building) > -1">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="#990066" viewBox="0 0 448 512"><path d="M438.6 105.4C451.1 117.9 451.1 138.1 438.6 150.6L182.6 406.6C170.1 419.1 149.9 419.1 137.4 406.6L9.372 278.6C-3.124 266.1-3.124 245.9 9.372 233.4C21.87 220.9 42.13 220.9 54.63 233.4L159.1 338.7L393.4 105.4C405.9 92.88 426.1 92.88 438.6 105.4H438.6z"/></svg>
                            </span>
                        </li>
                    </ul>

                    <h2 class="mt-5 font-bold">Floors</h2>
                    <ul>
                        <li :class="[{ 'border-uknar-primary': isFloorSelected(floor) }, 'text-sm border py-2 px-4 mt-2']"
                            @click="handleFloorSelect(floor)"
                            v-for="floor of filteredFloors">
                            {{ floor }}

                            <span class="selected-floor" v-show="selectedFloors.indexOf(floor) > -1">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="#990066" viewBox="0 0 448 512"><path d="M438.6 105.4C451.1 117.9 451.1 138.1 438.6 150.6L182.6 406.6C170.1 419.1 149.9 419.1 137.4 406.6L9.372 278.6C-3.124 266.1-3.124 245.9 9.372 233.4C21.87 220.9 42.13 220.9 54.63 233.4L159.1 338.7L393.4 105.4C405.9 92.88 426.1 92.88 438.6 105.4H438.6z"/></svg>
                            </span>
                        </li>
                    </ul>
                </div>
                <div class="text-center">
                    <button @click="closeModal" class="w-full sm:w-auto inline-flex justify-center py-2 px-4 border border-uknar-primary-dark shadow-sm text-sm font-medium rounded-md text-white bg-uknar-primary hover:bg-uknar-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-uknar-primary">
                        Search
                    </button>
                </div>
            </template>
        </fullscreen-modal>
    </div>
</template>

<script>
import FullscreenModal from "../../components/FullscreenModal";
import InputField from "../../components/form/InputField";
import _ from 'lodash';
export default {
    name: 'SearchContainer',
    components: { InputField, FullscreenModal },
    props: {
        floors: {
            type: Array,
            required: true
        },
        buildings: {
            type: Array,
            required: true
        }
    },

    mounted() {
        const el = document.getElementById('btn-search');
        const offsetTop = el.offsetTop;
        window.addEventListener('scroll', (evt) => {
            window.makeElementSticky(el, offsetTop);
        });
    },

    computed: {
        searchFieldPlaceholder() {
            if (!this.selectedFloors.length && !this.selectedBuildings.length) {
                return 'Filter by Building or Floor';
            }

            let placeholder = '';
            if (this.selectedFloors.length > 0) {
                placeholder = 'Filtering by ' + this.selectedFloors.join(', ') + ' floor(s)';
            }

            if (this.selectedBuildings.length > 0) {
                placeholder += ' for ' + this.selectedBuildings.join(', ') + ' building(s)';
            }

            if (!this.selectedFloors.length && this.selectedBuildings.length) {
                placeholder = 'Filtering by ' + this.selectedBuildings.join(', ') + ' building(s)';
            }

            return placeholder;
        },

        filteredFloors() {
            return _.filter(this.floors, (item) => item.indexOf(this.keyword) > -1);
        },

        filteredBuildings() {
            return _.filter(this.buildings, (item) => item.indexOf(this.keyword) > -1);
        }
    },

    data() {
        return {
            isModalOpen: false,
            selectedFloors: [],
            selectedBuildings: [],
            keyword: ''
        }
    },

    methods: {
        isBuildingSelected(building) {
            return this.selectedBuildings.indexOf(building) > -1;
        },

        isFloorSelected(floor) {
            return this.selectedFloors.indexOf(floor) > -1;
        },

        selectBuilding(building) {
            this.selectedBuildings.push(building);
            this.$emit('input', { buildings: this.selectedBuildings, floors: this.selectedFloors });
        },

        deselectBuilding(building) {
            this.selectedBuildings.splice(this.selectedBuildings.indexOf(building), 1);
            this.$emit('input', { buildings: this.selectedBuildings, floors: this.selectedFloors });
        },

        handleBuildingSelect(building) {
            if (this.isBuildingSelected(building)) {
                this.deselectBuilding(building);
            } else {
                this.selectBuilding(building);
            }
        },

        selectFloor(floor) {
            this.selectedFloors.push(floor);
            this.$emit('input', { buildings: this.selectedBuildings, floors: this.selectedFloors });
        },

        deselectFloor(floor) {
            this.selectedFloors.splice(this.selectedFloors.indexOf(floor), 1);
            this.$emit('input', { buildings: this.selectedBuildings, floors: this.selectedFloors });
        },

        handleFloorSelect(floor) {
            if (this.isFloorSelected(floor)) {
                this.deselectFloor(floor);
            } else {
                this.selectFloor(floor);
            }
        },

        openModal() {
            this.isModalOpen = true;
        },

        closeModal() {
            this.isModalOpen = false;
        }
    }
}
</script>

<style scoped>
    .selected-floor, .selected-building {
        width: 16px;
        height: 16px;
        float: right;
    }

    @media (max-width: 640px) {
        #btn-search.el-sticky {
            left: 0;
            border-radius: 0;
            z-index: 1;
        }
    }
</style>
